import { HeadingTag } from '@/globals/HeadingTag';
import { Component, Element, Prop, h, State } from '@stencil/core';

function userPrefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

@Component({
  tag: 'pn-media-block',
  styleUrl: 'pn-media-block.scss',
})
export class PnmediaBlock {
  @Prop() blockHeight: string = null;
  @Prop() imageSrc?: string = null;
  @Prop() imageSrcSmallScreenFormat?: string = undefined;
  @Prop() imageAltText?: string = '';
  @Prop() videoSrc?: string = null;
  @Prop() parallaxScroll: boolean = false;
  @Prop() showOverlayLayer: boolean = false;
  @Prop() customOverlayBackground?: string = null;
  @Prop() animateOverlayBackground: boolean = false;
  @Prop() scrollSnapBlock: boolean = false;
  @Prop() fixedBackground: boolean = false;
  @Prop() blockHeading?: string = null;
  @Prop() blockHeadingLevel?: string = 'h2';
  @Prop() blockPreamble?: string = null;
  @Prop() marginBottom?: boolean = false;

  @State() pnPlayOnScrollObserverOptions: IntersectionObserverInit = {
    root: document,
    threshold: 1,
    rootMargin: '220px 0px 220px 0px',
  };

  @State() calculatedBlockHeight: number;
  @State() hasButtonSlot: boolean = true;

  @Element() block: HTMLElement;

  get hasVideo() {
    if (this.videoSrc !== null && this.videoSrc !== 'undefined') {
      return true;
    }
    return false;
  }

  get hasImage() {
    if (this.imageSrc !== null && this.imageSrc !== 'undefined') {
      return true;
    }
    return false;
  }

  private observerScroll: IntersectionObserver;
  private observerSnap: IntersectionObserver;

  componentWillLoad() {
    this.hasButtonSlot = !!this.block.querySelector('[slot="cta-button"]');
  }

  async componentDidLoad() {
    const scrollTrigger = document.getElementById('scroll-trigger');
    this.calculatedBlockHeight = this.block.offsetHeight;

    const overlayElement = this.block.querySelector('#mediaBlockOverlay') as HTMLElement;
    if (this.customOverlayBackground != null || this.customOverlayBackground != 'undefined') {
      overlayElement.style.background = `${this.customOverlayBackground}`;
    }

    if (this.parallaxScroll) {
      this.observerScroll = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              window.addEventListener('scroll', this.handleScroll);
            } else {
              window.removeEventListener('scroll', this.handleScroll);
            }
          });
        },
        { rootMargin: `0px 0px` },
      );

      this.observerScroll.observe(scrollTrigger);
    }

    if (this.scrollSnapBlock) {
      this.observerSnap = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              // The block is within 100px of the viewport, scroll it into view
              if (this.block instanceof HTMLElement) {
                setTimeout(() => {
                  this.block.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 1000); // Adjust the delay as needed
              } else {
                console.error('this.block is not a valid HTMLElement');
              }
            }
          });
        },
        {
          root: document,
          rootMargin: '100px 0px 100px 0px',
          threshold: 0,
        },
      );

      this.observerSnap.observe(scrollTrigger);
    }
  }

  disconnectedCallback() {
    // Clean up the observer and event listener when the component is removed
    if (this.observerScroll) {
      this.observerScroll.disconnect();
    }
    if (this.observerSnap) {
      this.observerSnap.disconnect();
    }
    window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = () => {
    const mediaBlockElement = this.block.firstElementChild as HTMLElement;
    const scrollTrigger = this.block.querySelector('#scroll-trigger') as HTMLElement;
    const videoPauseButton = this.block.querySelector('#overlayed-button-pause-video') as HTMLButtonElement;

    const scrollTriggerTop = scrollTrigger.getBoundingClientRect().top;
    const mediaBlockElementHeight = mediaBlockElement.offsetHeight;

    const scrollPosition = mediaBlockElementHeight - scrollTriggerTop;

    const parallaxSpeedContent = 0.3;
    const parallaxSpeedMedia = 0.2;

    const mediaContainer = this.block.querySelector('.media-block__media-container') as HTMLElement;
    const content = this.block.querySelector('.media-block__content-container') as HTMLElement;

    if (mediaContainer != null) {
      // Move the image or video at a slower speed
      mediaContainer.style.transform = `translate(0, ${scrollPosition * (0.1 + parallaxSpeedMedia)}px)`;

      if (videoPauseButton != null) {
        // Move the pause button up
        videoPauseButton.style.transform = `translate(0, ${-scrollPosition * (0.3 + parallaxSpeedMedia)}px)`;
      }
    }

    if (content) {
      // Move the content at a faster speed
      content.style.transform = `translate(0, ${scrollPosition * (0.5 + parallaxSpeedContent)}px)`;

      // Change the opacity based on the scroll position
      const opacity = Math.max(0, 1 - scrollPosition / 600);
      content.style.opacity = `${opacity}`;
    }
  };

  render() {
    const { showOverlayLayer, fixedBackground, blockHeight, scrollSnapBlock, hasButtonSlot, hasVideo, hasImage, animateOverlayBackground, marginBottom } = this;

    const isVideo = hasVideo;
    const isImage = hasImage;

    const classes = [
      'media-block',
      blockHeight ? `media-block--height-${blockHeight}` : '',
      showOverlayLayer ? 'media-block--with-overlay' : '',
      animateOverlayBackground ? `media-block--animate-overlay-background` : '',
      isVideo ? 'media-block--video' : '',
      scrollSnapBlock ? 'scroll-snap' : 'no-scroll-snap',
      fixedBackground ? 'media-block--fixed-background' : '',
      marginBottom ? 'media-block--margin-bottom' : '',
    ].join(' ');

    const pnPlayOnScrollObserverOptions: IntersectionObserverInit = {
      root: this.blockHeight === '100svh' ? this.block : null,
      threshold: [0, 0.5, 1], // Callback will be executed when 0%, 50%, and 100% of the target is visible
    };

    return (
      <div class={classes}>
        <div class="media-block__block-container">
          <div class="media-block__media-container">
            {isVideo ? (
              <pn-play-on-scroll show-overlay={this.showOverlayLayer} observerOptions={pnPlayOnScrollObserverOptions} video-src={this.videoSrc}></pn-play-on-scroll>
            ) : isImage ? (
              <picture>
                <source media="(min-width: 1400px)" srcSet={this.imageSrc} />
                <source media="(min-width: 1224px)" srcSet={this.imageSrc} />
                <source media="(min-width: 1140px)" srcSet={this.imageSrc} />
                <source
                  media="(min-width: 1023px)"
                  srcSet={this.imageSrcSmallScreenFormat && this.imageSrcSmallScreenFormat !== 'undefined' ? this.imageSrcSmallScreenFormat : this.imageSrc}
                />
                <source
                  media="(min-width: 768px)"
                  srcSet={this.imageSrcSmallScreenFormat && this.imageSrcSmallScreenFormat !== 'undefined' ? this.imageSrcSmallScreenFormat : this.imageSrc}
                />
                <source
                  media="(min-width: 500px)"
                  srcSet={this.imageSrcSmallScreenFormat && this.imageSrcSmallScreenFormat !== 'undefined' ? this.imageSrcSmallScreenFormat : this.imageSrc}
                />
                <img
                  src={this.imageSrcSmallScreenFormat !== 'undefined' ? this.imageSrcSmallScreenFormat : this.imageSrc}
                  alt={this.imageAltText !== 'undefined' ? this.imageAltText : ''}
                  role={this.imageAltText !== 'undefined' ? 'img' : 'presentation'}
                  aria-hidden={this.imageAltText !== 'undefined' ? 'false' : 'true'}
                />
              </picture>
            ) : null}
          </div>

          {!isVideo && <div class="media-block__overlay-filter" id="mediaBlockOverlay"></div>}

          {(this.blockHeading || this.blockPreamble) && (
            <div class="media-block__content-container">
              {this.blockHeading && (
                <HeadingTag level={this.blockHeadingLevel} cssClass="media-block__block-heading">
                  {this.blockHeading}
                </HeadingTag>
              )}
              {this.blockPreamble && <p class="media-block__preamble">{this.blockPreamble}</p>}
              {hasButtonSlot && (
                <div class="media-block__cta-link">
                  <slot name="cta-button" />
                </div>
              )}
            </div>
          )}
        </div>

        <div id="scroll-trigger"></div>
      </div>
    );
  }
}
